import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The "Log in with HubSpot" page (M20 T1.4).
 *
 * Shown to logged-out users (expired/absent session on an installed portal).
 * Fetches the HubSpot authorize URL from /api/auth/login-url (a public route)
 * and redirects the browser there on click. HubSpot OAuth consent is instant
 * for an installed app, so this is effectively one click.
 *
 * This page has NO session (it's the entry point for getting one), so it does
 * not go through the /api proxy's session gate — login-url is in the public
 * auth allowlist.
 */
export function LoginPage() {
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // M30: friendly notice for Stripe-first arrivals — they installed the app
  // from Stripe's marketplace before signing in, and the install callback
  // sent them here (see src/handlers/stripeApp.ts, no-state branch).
  const [searchParams] = useSearchParams();
  const stripeNotice = searchParams.get('stripe_notice');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/login-url')
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load login URL (${res.status})`);
        const data = (await res.json()) as { url?: string; };
        if (cancelled) return;
        if (!data.url) throw new Error('Login URL missing in response');
        setLoginUrl(data.url);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="card mx-auto max-w-md p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10 text-brand-600">
        <HubSpotGlyph className="h-7 w-7" />
      </div>
      <h1 className="text-lg font-semibold text-ink-900">Log in</h1>
      {stripeNotice && (
        <p className="mt-3 rounded bg-brand-500/10 px-3 py-2 text-xs text-brand-700">
          {stripeNotice}
        </p>
      )}
      <p className="mt-2 text-sm text-ink-500">
        Continue with your HubSpot account. If Stripe Sync isn't installed in
        your portal yet, you'll be guided through installing it and starting
        your 30-day free trial. Each team member signs in with their own
        HubSpot account.
      </p>

      {error && (
        <p className="mt-4 rounded bg-warn-500/10 px-3 py-2 text-xs text-warn-700">
          {error}
        </p>
      )}

      <a
        href={loginUrl ?? '#'}
        className={`btn-primary mt-5 inline-flex items-center justify-center ${loginUrl ? '' : 'pointer-events-none opacity-60'
          }`}
        aria-disabled={!loginUrl}
      >
        {loginUrl ? 'Log in with HubSpot' : 'Loading…'}
      </a>

      <p className="mt-4 text-xs text-ink-400">
        You'll need HubSpot install rights (typically a super admin) to add
        Stripe Sync to your portal.
      </p>
    </div>
  );
}

/** Minimal HubSpot-style glyph (an orange hub). No external asset. */
function HubSpotGlyph({ className }: { className?: string; }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="17" cy="14" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="7" cy="7" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M9 8.2 15 12M14.5 6.2v6M9.2 7l6.4 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
